import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import withApiError from 'layers/errorHandling/apiError/component'
import {
  ERROR_TYPE_CONTENTFUL,
  SHOW_CONTENT_ON_401,
} from 'layers/errorHandling/apiError'
import { selectors as siteSelectors } from 'store/siteManager'
import {
  operations as ssapiOperations,
  selectors as ssapiSelectors,
} from 'store/ssapiManager'
import { Formik } from 'formik'
import { PLAYBOOK_STEP_MAP } from 'utils/adminExperienceHelpers'
import { validationSchema, fieldNames } from './data'

import PurposeModalComponent from './component'

export const PurposeModalContainer = ({
  clearErrors,
  fetchPublishFlows,
  flowLabel,
  handleCancel,
  hasApiError,
  handleClose,
  hideOutro,
  modals,
  site,
  structureVersion,
  postPublishFlows,
  publishFlowId,
  purpose,
  purposeIsCompleted,
  updatePublishFlows,
}) => {
  const isMountedRef = useRef(true)
  const [currentStep, setCurrentStep] = useState(purposeIsCompleted ? 1 : 0)

  useEffect(() => {
    if (site?.id) {
      fetchPublishFlows(site.id)
    }
  }, [site?.id])

  useEffect(() => {
    // Track mount status
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [publishFlowId])

  const handleStepChange = toStep => setCurrentStep(toStep)
  const goBack = () => {
    handleStepChange(currentStep - 1)
  }

  const handleSave = async (values, isComplete) => {
    const postBody = {
      flowName: PLAYBOOK_STEP_MAP.flowName,
      name: PLAYBOOK_STEP_MAP.name,
      publishDate: PLAYBOOK_STEP_MAP.publishDate,
      siteId: site.id,
      structureVersion,
    }
    const updateBody = {
      id: publishFlowId,
      siteId: site.id,
      items: [],
    }
    const completeOrDraftField = isComplete ? 'completeValue' : 'draftValue'

    updateBody.items = fieldNames.map(field => ({
      name: field,
      [completeOrDraftField]: values[field],
    }))

    if (!publishFlowId) {
      const { id } = await postPublishFlows(postBody)
      updateBody.id = id
      await updatePublishFlows(id, updateBody)
    } else {
      await updatePublishFlows(publishFlowId, updateBody)
    }
  }

  const closeModal = () => {
    clearErrors()
    handleClose()
  }

  const initialValues = {
    Purpose: purpose || '',
  }

  return (
    <Formik
      enableReinitialize={true}
      initialIsValid={validationSchema.isValidSync(initialValues)}
      initialTouched={{
        Purpose: !!purpose,
      }}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        setFieldTouched,
        setSubmitting,
        setTouched,
        isSubmitting,
        isValid,
      }) => (
        <PurposeModalComponent
          currentStep={currentStep}
          errors={errors}
          flowLabel={flowLabel}
          goBack={() => goBack()}
          handleCancel={handleCancel}
          handleCancelButtonClick={async (shouldSave, isComplete) => {
            if (shouldSave) {
              setSubmitting(true)
              await handleSave(values, isComplete)
              setSubmitting(false)
            }
            closeModal()
          }}
          handleChange={(name, value) => {
            setFieldTouched(name)
            setFieldValue(name, value)
          }}
          handleClose={closeModal}
          handleNextButtonClick={async (shouldSave, isComplete) => {
            const isValidSync = validationSchema.isValidSync(values)
            if (shouldSave && !isValidSync) {
              const touchedFields = fieldNames.reduce((acc, name) => {
                acc[name] = true
                return acc
              }, {})
              await setTouched(touchedFields)
              return
            }
            if (shouldSave) {
              setSubmitting(true)
              await handleSave(values, isComplete)
              setSubmitting(false)
            }
            if (purposeIsCompleted) {
              closeModal()
            } else {
              handleStepChange(currentStep + 1)
            }
          }}
          hasApiError={hasApiError}
          hideOutro={hideOutro}
          isSubmitting={isSubmitting}
          isValid={isValid}
          modals={modals}
          purposeIsCompleted={purposeIsCompleted}
          touched={touched}
          values={values}
        />
      )}
    </Formik>
  )
}

const mapStateToProps = (state, ownProps) => {
  const site = siteSelectors?.selectSiteById(state, ownProps.siteId)
  const { error, structureVersion } = state.ssapiManager
  const publishFlows = ssapiSelectors.selectPublishFlowsBySiteId(
    state,
    ownProps.siteId,
  )
  const publishFlow =
    publishFlows?.find(
      flow => flow.publishFlowStructureId === structureVersion,
    ) || {}
  const publishFlowId = publishFlow?.id || null
  const purposeFlowItem = publishFlow?.publishFlowItems?.find(
    item => item.name === 'Purpose',
  )
  const purpose =
    purposeFlowItem?.completeValue || purposeFlowItem?.draftValue || null
  const purposeIsCompleted =
    purpose?.length > 0 &&
    purposeFlowItem?.completeValue !== null &&
    purposeFlowItem?.completeValue?.length > 0

  return {
    hasApiError: !!error,
    publishFlowId,
    purpose,
    purposeIsCompleted,
    site,
    structureVersion,
  }
}

const mapDispatchToProps = {
  clearErrors: ssapiOperations.clearErrors,
  fetchPublishFlows: ssapiOperations.fetchPublishFlows,
  postPublishFlows: ssapiOperations.postPublishFlows,
  updatePublishFlows: ssapiOperations.updatePublishFlows,
}

PurposeModalContainer.propTypes = {
  clearErrors: PropTypes.func,
  dispatch: PropTypes.func,
  fetchPublishFlows: PropTypes.func,
  flowLabel: PropTypes.string,
  handleCancel: PropTypes.func,
  handleClose: PropTypes.func,
  hasApiError: PropTypes.bool,
  hideOutro: PropTypes.bool,
  modals: PropTypes.array,
  postPublishFlows: PropTypes.func,
  publishFlowId: PropTypes.number,
  purpose: PropTypes.string,
  purposeIsCompleted: PropTypes.bool,
  site: PropTypes.shape({
    id: PropTypes.number,
    sitePreferences: PropTypes.object,
  }),
  structureVersion: PropTypes.number,
  updatePublishFlows: PropTypes.func,
}

const mapper = entry => {
  const { modals, flowLabel } = entry || {}
  return {
    modals,
    flowLabel,
  }
}

const options = {
  entryId: '3BsWYN9mcO7XwBukv2hvR2',
  include: 3,
  mapper,
  spread: true,
}

export default ContentEntryDataGetter(
  withApiError(
    PurposeModalContainer,
    [ERROR_TYPE_CONTENTFUL],
    [SHOW_CONTENT_ON_401],
  ),
  options,
  connect(mapStateToProps, mapDispatchToProps),
)
